import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    Output,
} from '@angular/core';
import {
    type ImageFormControl,
    type ProductCardDataFormType,
    type ProductCardFormType,
    type VariantFormGroup,
} from '@web/widgets/product-card/type';
import {
    imageFormControl,
    propertyFormGroup,
    variantFormGroup,
} from '@web/widgets/product-card/form/product-card-widget.form';
import {
    CdkDrag,
    type CdkDragDrop,
    CdkDragHandle,
    CdkDropList,
    CdkDropListGroup,
    moveItemInArray,
} from '@angular/cdk/drag-drop';
import { type AbstractControl, FormArray, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { type SelectedFileExtended } from '@common/types/file.type';
import { extendWebFileDataPromise } from '@common/helpers';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { type SelectedFileInterface } from '@sendpulse/file-manager';
import { environment } from '@web-env/environment';
import { TranslateModule } from '@ngx-translate/core';
import { SpClickOutsideDirective } from '@common/directives/click-outside.directive';
import { SpFileManagerComponentModule } from '@components/sp-file-manager/src/sp-file-manager.module';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { CommonModule } from '@angular/common';
import { formatFileUrl } from '@components/sp-file-manager/src/helpers';

@Component({
    selector: 'web-product-variant',
    templateUrl: './product-variant.component.html',
    styleUrls: ['./product-variant.component.less'],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
        CommonModule,
        CdkDropList,
        CdkDrag,
        CdkDropListGroup,
        CdkDragHandle,
        FormsModule,
        ReactiveFormsModule,
        TooltipModule,
        SpFileManagerComponentModule,
        SpClickOutsideDirective,
        TranslateModule,
    ],
})
export class ProductVariantComponent {
    protected readonly websiteFacade = inject(SiteFacade);
    protected readonly changeDetectorRef = inject(ChangeDetectorRef);

    @Input() variants: ProductCardFormType['variants'];
    @Input() properties: ProductCardFormType['properties'];
    @Input() element: ProductCardDataFormType['element'];
    @Input() hasPropertiesInEditError: boolean = false;
    @Output() hasPropertiesInEditErrorChange = new EventEmitter<boolean>();

    oldPropertyValue: {
        name: string;
        parameters: string[];
        isEdit: boolean;
    };

    private isNewProperty = false;

    public addVariant(): void {
        this.hasPropertiesInEditErrorChange.emit(false);
        this.properties.push(propertyFormGroup());
        this.isNewProperty = true;
    }

    public editProperty(index: number) {
        this.isNewProperty = false;
        this.oldPropertyValue = this.properties.at(index).getRawValue();

        this.properties.controls.forEach((property, i) => {
            const equals = i === index;
            return property.controls.isEdit.setValue(equals ?? true);
        });
    }

    public removeProperty(index: number) {
        this.properties.removeAt(index);

        this.generateTableList();
    }

    public drop(event: CdkDragDrop<ProductCardFormType['properties']>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        moveItemInArray(this.properties.controls, event.previousIndex, event.currentIndex);
        this.properties.patchValue(this.properties.controls.map((control) => control.getRawValue()));
    }

    public editName(value: string, index: number) {
        this.properties.at(index).controls.name.patchValue(value);
    }

    public editParams(value: string, index: number) {
        this.properties.at(index).controls.parameters.patchValue(value.split(','));
    }

    public cancelEdit(index: number) {
        if (this.isNewProperty) {
            this.properties.removeAt(index);
            return;
        }

        this.properties.at(index).patchValue({ ...this.oldPropertyValue, isEdit: false });
    }

    public save(index: number) {
        const property = this.properties.at(index);
        const name = property.controls.name.value?.trim();
        const parameters = property.controls.parameters.value;

        if (!name || !parameters?.length || parameters.every((p) => !p.trim())) {
            this.hasPropertiesInEditErrorChange.emit(true);
            return;
        }

        this.hasPropertiesInEditErrorChange.emit(false);
        property.controls.isEdit.patchValue(false);
        this.generateTableList();
    }

    public generateTableList() {
        const newVariantsArray = new FormArray<VariantFormGroup>([]);

        const combinedParameters: Array<string[]> = this.properties.controls.reduce((accumulator, property) => {
            const parameters = property.get('parameters').value;
            if (Array.isArray(parameters)) {
                accumulator.push(parameters);
            }

            return accumulator;
        }, []);

        const variantsNameArray = this.generateVariantsNameArray(combinedParameters);

        variantsNameArray?.forEach((variantName) => {
            const commonVariant = this.variants.controls.find((variant) => variantName.includes(variant.value.name));
            if (commonVariant) {
                const existingVariant = variantFormGroup({
                    name: variantName,
                    properties: variantName.split('/'),
                    vendorCode: commonVariant.value.vendorCode.value,
                    price: commonVariant.value.price.value,
                    oldPrice: commonVariant.value.oldPrice.value,
                    quantity: commonVariant.value.quantity.value,
                    isShow: commonVariant.value.isShow,
                    images: this.variantImagesControls(commonVariant),
                });
                newVariantsArray.push(existingVariant);
            } else {
                newVariantsArray.push(
                    variantFormGroup({
                        name: variantName,
                        vendorCode: this.element.value.vendorCode,
                        price: this.element.value.price,
                        oldPrice: this.element.value.oldPrice,
                        quantity: this.element.value.quantity,
                        images: this.imagesControls,
                        properties: variantName.split('/'),
                    }),
                    { emitEvent: false }
                );
            }
        });

        this.variants.clear({ emitEvent: false });
        newVariantsArray.controls.forEach((control) => this.variants.push(control));
    }

    public changeCheckBoxValue(index: number): void {
        const control = this.variants.at(index).controls.isShow;
        control.patchValue(!control.value);
    }

    public setEditingField(control: AbstractControl, field?): void {
        control.patchValue(!control.value);

        if (!control.value) {
            return;
        }

        const editingField: HTMLElement = field?.currentTarget?.parentNode?.querySelector('.variant-cell-editing');
        const editingFieldInput: HTMLElement = editingField?.querySelector('input');

        setTimeout(() => editingFieldInput?.focus(), 100);
    }

    public async addImage(files: SelectedFileInterface[]): Promise<void> {
        if (!files) return;

        for (const file of files) {
            const extendedFile: SelectedFileExtended = await extendWebFileDataPromise(
                file,
                environment.fileManagerCdnUrl
            );

            this.cardImages.push(imageFormControl(extendedFile));

            this.websiteFacade.resizeImage({
                path: extendedFile.path,
                url: `${environment.fileManagerCdnUrl}${extendedFile.url}`,
            });
        }

        this.changeDetectorRef.detectChanges();
    }

    public setVariantImages(variantIndex: number, image: SelectedFileExtended) {
        if (this.variants.at(variantIndex).controls.images.value.includes(image)) {
            const index = this.variants
                .at(variantIndex)
                .controls.images.value.findIndex((variantImage) => variantImage === image);

            this.variants.at(variantIndex).controls.images.removeAt(index);
        } else {
            this.variants.at(variantIndex).controls.images.push(imageFormControl(image));
        }
    }

    public isSelectedImage(variantIndex: number, image: SelectedFileExtended): boolean {
        return this.variants.at(variantIndex).controls.images.value.includes(image);
    }

    public formatImageURL(image: SelectedFileExtended): string {
        return formatFileUrl(image, environment.fileManagerCdnUrl);
    }

    public handleImageEdit(index: number) {
        this.variants.at(index).controls.isImageEdit.patchValue(!this.variants.at(index).controls.isImageEdit.value);
    }

    private generateVariantsNameArray(arrays: Array<string[]>, separator = '/'): string[] {
        if (arrays.length === 0) {
            return [];
        }

        const result = [];
        function combine(index, currentCombination) {
            if (index === arrays.length) {
                result.push(currentCombination.join(separator));
                return;
            }

            for (const item of arrays[index]) {
                combine(index + 1, [...currentCombination, item.trim()]);
            }
        }

        combine(0, []);
        return result;
    }

    public selectedPreviewImage(index: number): ImageFormControl {
        return this.variants.at(index)?.controls?.images.length
            ? this.variants.at(index)?.controls?.images.controls[0]
            : this.cardImages.controls[0];
    }

    public get cardImages(): ProductCardFormType['images'] {
        return this.element.get('images') as ProductCardFormType['images'];
    }

    private get imagesControls(): ImageFormControl[] {
        const controls: ImageFormControl[] = [];
        this.element.controls.images.value.forEach((image) => controls.push(imageFormControl(image)));

        return controls;
    }

    private variantImagesControls(element: VariantFormGroup): ImageFormControl[] {
        const controls: ImageFormControl[] = [];
        element.controls.images.value.forEach((image) => controls.push(imageFormControl(image)));

        return controls;
    }

    public normalizeNonNegativeNumber(control: AbstractControl): void {
        const raw = control?.value;

        if (raw === '' || raw === null || raw === undefined) {
            if (control.value !== null) control.patchValue(null, { emitEvent: false });
            return;
        }

        const num = Number(raw);

        if (!isFinite(num) || isNaN(num) || num < 0) {
            if (control.value !== null) control.patchValue(null, { emitEvent: false });
            return;
        }

        if (control.value !== num) {
            control.patchValue(num, { emitEvent: false });
        }
    }

    public get hasPropertiesInEdit(): boolean {
        return this.properties.controls.some((property) => property.value?.isEdit);
    }
}
